//Hooks
import { get } from 'svelte/store';
//Helpers
import { home_data } from 'src/stores';
import { isPremiumUser } from 'src/helpers/user_helpers';
import { addDate, timeFormatUS } from 'src/helpers/date_helper';
import { getClickAction, getDeviceTimeZoneDate } from '../core/chart_helper';
import { getUserTimeZoneDate } from 'src/helpers/date_helper';
import {
	buildChartRangeControls,
	LAYER_RANGE_OPTIONS,
	rangeDates,
	type ChartControlsDataInterface,
	type RangeOptionInterface,
} from './chart_range_helpers';
import type { ChartDataInterface } from 'src/types/Chart';
import { is_GBLE } from 'src/helpers/interface_helpers';
//Types
import type { ChartConfig, ChartStatisticRangePeriod, TimeZoneInterface } from 'src/types/Chart';
import type { Device } from 'src/types/Device';

export { buildChartRangeControls, LAYER_RANGE_OPTIONS, rangeDates };
export type { ChartControlsDataInterface, RangeOptionInterface };

const TIME_LABEL_FORMATS = {
	hour: 'H:i',
	day: 'H:i',
	week: 'D d M',
	month: 'd.M',
	day_splitter: 'd.M',
	year: 'M`Y',
	day_of_week: 'D',
	tariff_day_of_week: 'D H:i',
	'7_days': 'D d M',
	'30_days': 'd.M',
	'6_months': 'M`Y',
	'12_months': 'M`Y',
};

export const RANGE_OPTIONS: Array<RangeOptionInterface> = [
	{
		period: '24_hours',
		timeRange: '24h',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'day',
		timeRange: '1d',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'hour',
		timeRange: '1h',
		timeLabelFormat: 'hour',
		enabled: true,
	},

	{
		period: '7_days',
		timeRange: '7d',
		timeLabelFormat: 'week',
		enabled: true,
	},

	{
		period: 'week',
		timeRange: '1w',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: '30_days',
		timeRange: '30d',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: 'month',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: '12_months',
		timeRange: '12m',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'year',
		timeRange: '1y',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'billing_date',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: 'custom',
		timeRange: 'custom',
		timeLabelFormat: 'custom',
		enabled: true,
	},
];

export const getTimeLabelFormat = (timeLabelFormat: string) => {
	let timeFormat = TIME_LABEL_FORMATS[timeLabelFormat] || TIME_LABEL_FORMATS.day;
	if (/H:i$/.test(timeFormat) && timeFormatUS()) {
		timeFormat = timeFormat.replace('H', 'h') + ' A';
	}
	return timeFormat;
};

export const getInitLoadedPeriodStatistics = (
	rangeOpt: RangeOptionInterface[],
): RangeOptionInterface => {
	let rangeOption: RangeOptionInterface = rangeOpt[0];
	const initPeriodKey = get(home_data)?.init_loaded_period_statistics;
	if (initPeriodKey) {
		rangeOption = rangeOpt.find((i) => i.period == initPeriodKey);
		if (!rangeOption?.enabled) rangeOption = rangeOpt[0];
	}
	return rangeOption;
};

export const isPrevPeriodComparisonEnabled = (): boolean => {
	return get(home_data)?.prev_period_comparison?.value;
};

export const getBillingDate = (): Date => {
	const today = new Date(),
		billingDate = get(home_data)?.billing_date || 1;
	let date = new Date();
	if (today.getDate() < billingDate) {
		date = addDate(today, 'm', -1);
	}
	date.setDate(billingDate);
	return date;
};

export const getEnabledHourControl = (): boolean => {
	return isPremiumUser();
};

export const buildOverallChartControlsData = (
	currentPeriod: ChartStatisticRangePeriod,
): ChartControlsDataInterface => {
	const timeZone = getUserTimeZoneDate();
	let date = timeZone?.date || new Date();
	if (currentPeriod === 'billing_date') {
		date = addDate(getBillingDate(), 'd', 0, true);
	}
	return buildChartRangeControls(currentPeriod, date);
};

export const resolveLayerChartDrillDownPeriod = (
	chartData: ChartDataInterface,
): ChartStatisticRangePeriod | null => {
	if (!chartData) return null;
	if (chartData.rangePeriod === 'custom') {
		return (chartData.rangeInterval as ChartStatisticRangePeriod) ?? null;
	}
	return (getClickAction(chartData.rangePeriod) as ChartStatisticRangePeriod) ?? null;
};

export const applyLayerChartDrillDown = (
	chartData: ChartDataInterface,
	clickedDate: Date,
): ChartControlsDataInterface | null => {
	const drillPeriod = resolveLayerChartDrillDownPeriod(chartData);
	if (!drillPeriod) return null;

	const rangeOption = LAYER_RANGE_OPTIONS.find((option) => option.period === drillPeriod);
	if (!rangeOption?.enabled) return null;

	const time = rangeDates(clickedDate, rangeOption, 'click', true);
	return {
		time,
		format: 'Y-m-d H:i:s',
		rangeOption,
		currentPeriod: drillPeriod,
	};
};

export const buildChartControlsData = (
	device: Device,
	currentPeriod: ChartStatisticRangePeriod,
): ChartControlsDataInterface => {
	const timeZone: TimeZoneInterface = getDeviceTimeZoneDate(device);
	let date = timeZone?.date || new Date();
	if (currentPeriod == 'billing_date') {
		date = addDate(getBillingDate(), 'd', 0, true);
	}
	return buildChartRangeControls(currentPeriod, date);
};

export type ChartMenuItem = {
	value: number;
	icon: string;
	title: string;
};

export const buildChartHeaderMenu = (chartConfigs: ChartConfig[]): ChartMenuItem[] => {
	return chartConfigs.map((item, index) => ({
		value: index,
		icon: item.icon,
		title: item.chartTitle,
	}));
};

export const isBleStatisticsDeviceToUTC = (
	device: Device,
	period: ChartStatisticRangePeriod,
): boolean => {
	return is_GBLE(device) && /^(24_hours|day|hour)$/.test(period);
};
